import type { FC } from 'react';
import React, { Fragment, useRef } from 'react';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import {
	HOME_RIGHT_PANEL_WELCOME_MESSAGE_EXPERIENCE,
	HOME_RIGHT_PANEL_APPS_EXPERIENCE,
	HOME_SPACES_EXPERIENCE,
	HOME_RIGHT_PANEL_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { collectAll } from '@confluence/experience-tracker/entry-points/ExperienceEventCollector.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';

import { HOME_OVERVIEW_RIGHT_PANEL_PAGE_SEGMENT } from '../../perf.config.ts';
import { useHomeWidgetState } from '../useHomeWidgetState.ts';

import { ForgeApps } from './ForgeApps.tsx';
import { RightPanelDragHandle } from './RightPanelDragHandle.tsx';
import { useRightPanelVisible } from './useRightPanelVisible.ts';
import { useRightPanelResize } from './useRightPanelResize.ts';

const DEFAULT_WIDTH = 475;
const MIN_WIDTH = 350;
const MAX_WIDTH = 818;
const POPULAR_COMPACT_THRESHOLD = 500;

type WidthMode = 'resizable' | 'fullwidth';

const resizeablePanelStyles = css({
	boxSizing: 'border-box',
	height: '100%',
	overflowY: 'auto',
	wordBreak: 'break-word',
	backgroundColor: token('elevation.surface.raised'),
	padding: token('space.150'),
});

const panelContainerStyles = css({
	boxSizing: 'border-box',
	height: 'calc(100% - 12px)',
	wordBreak: 'break-word',
	marginBottom: token('space.300'),
});

export type RightPanelProps = {
	widthMode?: WidthMode;
};

const collectRightPanelExperiences = (events, experience) => {
	collectAll([
		HOME_RIGHT_PANEL_WELCOME_MESSAGE_EXPERIENCE,
		HOME_RIGHT_PANEL_APPS_EXPERIENCE,
		HOME_SPACES_EXPERIENCE,
	])(events, experience);
};

export const RightPanel: FC<RightPanelProps> = ({ widthMode = 'resizable' }) => {
	const rightPanelRef = useRef<HTMLDivElement | null>(null);
	const { shouldShowForgeApps } = useRightPanelVisible();

	const { onResizeDrag, onResizeComplete, initialWidth } = useRightPanelResize({
		rightPanelRef,
		defaultWidth: DEFAULT_WIDTH,
		minWidth: MIN_WIDTH,
		maxWidth: MAX_WIDTH,
		compactThreshold: POPULAR_COMPACT_THRESHOLD,
	});

	// Query for widget state here so that the data is ready by the time the CollapsibleSections render
	const { error, loading: preferencesLoading } = useHomeWidgetState('right-panel');

	const widthStyle = useWidthStyle(widthMode, initialWidth);

	if (preferencesLoading) {
		return null;
	}

	return (
		<aside
			ref={rightPanelRef}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={widthStyle}
			css={[
				widthMode === 'resizable' && resizeablePanelStyles,
				widthMode !== 'resizable' && panelContainerStyles,
			]}
		>
			{widthMode === 'resizable' && (
				<RightPanelDragHandle onResizeDrag={onResizeDrag} onResizeComplete={onResizeComplete} />
			)}
			<ExperienceStart name={HOME_RIGHT_PANEL_EXPERIENCE} collect={collectRightPanelExperiences} />
			<Fragment>
				<div data-testid="right-panel-content">{shouldShowForgeApps && <ForgeApps />}</div>
				{error && <ErrorDisplay error={error} />}
				<PageSegmentLoadEnd metric={HOME_OVERVIEW_RIGHT_PANEL_PAGE_SEGMENT} />
			</Fragment>
		</aside>
	);
};

const useWidthStyle = (widthMode: WidthMode, initialWidth: number) => {
	const width = widthMode === 'fullwidth' ? 'auto' : initialWidth;
	return { width };
};
